import React, { useCallback, useContext, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import CommitIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import {
  BitbucketLink,
  IntervalRenderer,
} from 'src/components/pipelines/common';
import { DropdownMenuMockContext } from 'src/components/pipelines/contexts/DropdownMenuMockContext';
import { Pipeline, Repository } from 'src/components/pipelines/models';
import { requestStopPipeline } from 'src/components/pipelines/redux/actions/pipelines';
import {
  formatDate,
  formatDistanceToNow,
  links,
} from 'src/components/pipelines/utils';
import { useIntl } from 'src/hooks/intl';

import StatusIcon from '../../status-icon';
import formatDuration from '../utils/formatDuration';
import { getCommitsPageURL, getRefPageURL } from '../utils/links';

import BuildRefIcon from './BuildRefIcon';
import { messages } from './i18n';
import {
  AuthorCol,
  CommitMessage,
  InProgress,
  PipelineInfoLink,
  PipelineInfoText,
  PipelineLink,
  RowWrapper,
  ScheduledIcon,
} from './styled';

export type Props = {
  pipeline: Pipeline;
  repository: Repository;
  hasPermissionToStop: boolean;
  sendAnalyticsEvent: (action: string) => void;
};

export const InlineSettings = ({
  pipeline,
  sendAnalyticsEvent,
  hasPermissionToStop,
}: {
  pipeline: Pipeline;
  sendAnalyticsEvent: (action: string) => void;
  hasPermissionToStop: boolean;
}) => {
  const { defaultOpen } = useContext(DropdownMenuMockContext);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const onClick = useCallback(() => {
    sendAnalyticsEvent(messages.inlineStop.defaultMessage);
    dispatch(requestStopPipeline(pipeline.uuid));
  }, [dispatch, pipeline.uuid, sendAnalyticsEvent]);

  return (
    <DropdownMenu<HTMLButtonElement>
      defaultOpen={defaultOpen}
      trigger={({ triggerRef, ...props }) => (
        <Button
          {...props}
          iconBefore={<MoreIcon label="settings" size="small" />}
          label="Actions"
          appearance="subtle"
          ref={triggerRef}
          testId="pipelines-inline-action-button"
        />
      )}
      shouldRenderToParent
    >
      <DropdownItemGroup>
        <DropdownItem
          onClick={onClick}
          isDisabled={!pipeline.isRunning || !hasPermissionToStop}
        >
          {formatMessage(messages.inlineStop)}
        </DropdownItem>
      </DropdownItemGroup>
    </DropdownMenu>
  );
};

const PipelineRow = ({
  pipeline,
  repository,
  hasPermissionToStop,
  sendAnalyticsEvent,
}: Props) => {
  const { formatMessage } = useIntl();

  const currentDuration = useMemo(
    () =>
      pipeline.durationInSeconds || !pipeline.isRunning ? (
        formatDuration(pipeline.durationInSeconds)
      ) : (
        <InProgress aria-hidden="true" />
      ),
    [pipeline]
  );

  return (
    <RowWrapper className="row" data-testid="pipeline-row">
      <AuthorCol headers="pipeline">
        {pipeline.isScheduled ? (
          <ScheduledIcon>
            <CalendarIcon label="Scheduled" size="medium" />
          </ScheduledIcon>
        ) : (
          <Avatar src={pipeline.creator.avatarUrl} size="medium" />
        )}
      </AuthorCol>
      <td headers="pipeline">
        <CommitMessage title={pipeline.message}>
          <PipelineLink>
            <BitbucketLink
              to={links.getPipelinePageURL(pipeline, repository.path)}
              data-testid="pipeline-row-link"
            >
              {pipeline.message ? (
                pipeline.message
              ) : (
                <em>{pipeline.revision || 'Commit message unavailable'}</em>
              )}
            </BitbucketLink>
          </PipelineLink>
        </CommitMessage>
        <Inline alignBlock="center">
          <PipelineInfoText>#{pipeline.buildNumber} -</PipelineInfoText>
          {pipeline.isScheduled ? (
            <PipelineInfoText>Scheduled</PipelineInfoText>
          ) : (
            <PipelineInfoText>{pipeline.creator.displayName}</PipelineInfoText>
          )}
          <PipelineInfoLink
            href={getCommitsPageURL(repository.path, pipeline.revision)}
            target="_parent"
          >
            <CommitIcon label="Commit" size="small" />
            {(pipeline.revision || '').substr(0, 7)}
          </PipelineInfoLink>
          {pipeline.refName && (
            <Inline xcss={xcss({ marginRight: 'space.075' })}>
              <Tooltip content={pipeline.refName}>
                {props => (
                  <PipelineInfoLink
                    {...props}
                    href={getRefPageURL(
                      repository.path,
                      pipeline.refType,
                      pipeline.refName,
                      pipeline.revision
                    )}
                    target="_parent"
                  >
                    <BuildRefIcon type={pipeline.refType} /> {pipeline.refName}
                  </PipelineInfoLink>
                )}
              </Tooltip>
            </Inline>
          )}
          {pipeline.customName && (
            <Inline xcss={xcss({ marginRight: 'space.075' })}>
              <Tooltip content={pipeline.customName}>
                {props => (
                  <PipelineInfoLink
                    {...props}
                    href={getRefPageURL(
                      repository.path,
                      'custom',
                      pipeline.customName,
                      pipeline.revision
                    )}
                    target="_parent"
                  >
                    {pipeline.customName}
                  </PipelineInfoLink>
                )}
              </Tooltip>
            </Inline>
          )}
          {pipeline.selectorType === 'pull-requests' && (
            <Tooltip content={pipeline.pullrequest.title}>
              {props => (
                <PipelineInfoLink
                  {...props}
                  href={pipeline.pullrequest.url}
                  target="_parent"
                >
                  <BuildRefIcon type="pull-requests" />{' '}
                  {pipeline.pullrequest.title}
                </PipelineInfoLink>
              )}
            </Tooltip>
          )}
        </Inline>
      </td>
      <td headers="status">
        <BitbucketLink to={links.getPipelinePageURL(pipeline, repository.path)}>
          <StatusIcon
            status={pipeline.statusIcon}
            className="PipelineRowStatus"
            {...(pipeline.state.result?.name === 'STOPPED' &&
              pipeline.state.result.terminator?.display_name && {
                tooltipContent: formatMessage(messages.stoppedBy, {
                  name: pipeline.state.result.terminator.display_name,
                }),
                tooltipPosition: 'bottom',
              })}
          />
        </BitbucketLink>
      </td>
      <td headers="started" title={formatDate(pipeline.creationDate)}>
        <IntervalRenderer interval={15000}>
          {() => formatDistanceToNow(pipeline.creationDate)}
        </IntervalRenderer>
      </td>
      <td headers="duration">
        {pipeline.status === 'SYSTEM_ERROR' ? (
          <Tooltip content="Excluded from usage">
            <span>{currentDuration}</span>
          </Tooltip>
        ) : (
          <span>{currentDuration}</span>
        )}
      </td>
      <td headers="actions">
        <InlineSettings
          hasPermissionToStop={hasPermissionToStop}
          sendAnalyticsEvent={sendAnalyticsEvent}
          pipeline={pipeline}
        />
      </td>
    </RowWrapper>
  );
};

export default React.memo(PipelineRow);
